import { useEffect, useRef, useState } from 'react';
import { MenuItem, Paper, Popper, Typography, useTheme } from '@mui/material';
import { Box, Stack } from '@mui/system';

import { Flags } from '@watershed/constants/flags';
import ChevronRightIcon from '@watershed/icons/components/ChevronRight';
import mixinSx from '@watershed/style/mixinSx';
import Button from '@watershed/ui-core/components/Button';
import CircularProgress from '@watershed/ui-core/components/CircularProgress';
import BoxLink from '@watershed/ui-core/components/BoxLink';

import { useFeatureFlag } from '../../utils/FeatureFlag';
import { getSharedSidebarStyles } from '../navigation/variants/sharedSidebarStyles';
import { SIDEBAR_Z_INDEX } from './SideBar';
import { NavSectionItem } from './utils';

export default function NavMenu({
  name,
  items,
  handleNavMenuHover,
  loading,
  placeholderMessage,
}: NavSectionItem['menu'] & {
  handleNavMenuHover?: (hovered: boolean) => void;
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popperRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();
  const isLightTheme = useFeatureFlag(Flags.LightThemeSideNavigation);
  const sharedSidebarStyles = getSharedSidebarStyles(theme, isLightTheme);
  const { COLORS, BOX_SHADOW_FOCUS } = sharedSidebarStyles;

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
    handleNavMenuHover?.(true);
  };

  const handleMouseLeave = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(null);
    handleNavMenuHover?.(false);
  };

  const focusItems = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Tab' && anchorEl) {
      event.preventDefault();
      const firstMenuItem = popperRef.current?.querySelector(
        '#nav-menu [tabindex]:not([tabindex="-1"])'
      ) as HTMLElement;
      firstMenuItem?.focus();
    } else if (event.key === 'Escape') {
      setAnchorEl(null);
    }
  };

  const exitPopper = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      setAnchorEl(null);
    }
  };

  useEffect(() => {
    if (anchorEl && popperRef.current) {
      const firstFocusableElement = popperRef.current.querySelector(
        '#nav-menu [tabindex]:not([tabindex="-1"])'
      ) as HTMLElement;
      firstFocusableElement?.focus();
    }
  }, [anchorEl]);

  const handleMenuKeyDown = (
    event: React.KeyboardEvent<HTMLElement>,
    index: number
  ) => {
    if (event.key === 'Tab') {
      const isLastItem = index === items.length - 1;
      if (isLastItem && !event.shiftKey) {
        event.preventDefault();
        setAnchorEl(null);

        // Find the next focusable element in the NavSubSection
        const nextFocusableElement = buttonRef.current
          ?.closest('li')
          ?.nextElementSibling?.querySelector(
            '[tabindex]:not([tabindex="-1"])'
          ) as HTMLElement;

        if (nextFocusableElement) {
          nextFocusableElement.focus();
        } else {
          // If no next focusable element in the current section,
          // look for the next section
          const currentSection = buttonRef.current?.closest('section');
          const nextSection = currentSection?.nextElementSibling as HTMLElement;

          if (nextSection) {
            // Find the first li within the next section's ul
            const firstLiInNextSection = nextSection.querySelector(
              'ul > li'
            ) as HTMLElement;

            if (firstLiInNextSection) {
              // Focus on the first focusable element within the li
              const firstFocusableElement = firstLiInNextSection.querySelector(
                '[tabindex]:not([tabindex="-1"])'
              ) as HTMLElement;
              firstFocusableElement?.focus();
            }
          }
        }
      }
    }
  };

  const isOpen = Boolean(anchorEl);

  return (
    <Stack onMouseLeave={() => setAnchorEl(null)}>
      <Button
        ref={buttonRef}
        variant="text"
        size="small"
        aria-label="Open menu"
        startIcon={<ChevronRightIcon />}
        aria-controls="nav-menu"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onMouseEnter={handleMouseEnter}
        onClick={handleMouseEnter}
        onKeyDown={focusItems}
        sx={mixinSx(sharedSidebarStyles.iconButtonSxProps, {
          marginInlineEnd: -0.5,
          borderRadius: '4px',
          position: 'relative',
          zIndex: 1,
          opacity: 0,

          '[aria-current="page"] &, a:hover &, a:focus &, &[aria-expanded="true"], &:focus':
            {
              opacity: 1,
            },
          '[data-appears-collapsed="true"] &': {
            display: 'none',
          },

          // No hover/focus/open highlight on chevron to avoid clipped bounding box
          '&:hover, &:focus': {
            backgroundColor: 'transparent',
          },
          'a:hover &, a:focus-visible &': {
            backgroundColor: 'transparent',
          },

          '& .MuiButton-startIcon': {
            margin: 0,
            '& svg': {
              width: 16,
              height: 16,
            },
          },
          ...(isOpen && {
            '&:focus-visible': {
              boxShadow: BOX_SHADOW_FOCUS,
            },
          }),
        })}
        isIcon
      />
      <Popper
        id="nav-menu"
        sx={{
          // Make sure the z-index is always at least 1 more than the sidebar.
          zIndex: SIDEBAR_Z_INDEX(theme) + 1,
        }}
        open={isOpen}
        role="menu"
        anchorEl={anchorEl}
        placement="bottom-start"
        onMouseLeave={handleMouseLeave}
        onKeyDown={exitPopper}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [28, -48],
            },
          },
        ]}
      >
        <Paper
          ref={popperRef}
          sx={mixinSx(
            sharedSidebarStyles.menuPaperSxProps,
            sharedSidebarStyles.menuPaperListSxProps
          )}
          onMouseEnter={() => handleNavMenuHover?.(true)}
          onMouseLeave={handleMouseLeave}
          tabIndex={-1}
        >
          {name && (
            <Typography
              variant="body1"
              sx={{
                paddingInlineStart: 1.5,
                paddingBlockStart: 0.5,
                paddingBlockEnd: 0.5,
                color: COLORS.TEXT_SECONDARY,
              }}
            >
              {name}
            </Typography>
          )}
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
              sx={{ py: 2 }}
            >
              <CircularProgress
                size={24}
                thickness={5}
                sx={{ color: COLORS.TEXT_SECONDARY }}
              />
            </Box>
          ) : placeholderMessage ? (
            <Typography
              variant="body1"
              sx={{
                paddingInlineStart: 1.5,
                paddingBlockStart: 0.5,
                color: COLORS.TEXT_PRIMARY,
              }}
            >
              {placeholderMessage}
            </Typography>
          ) : (
            items.map((item, index) => (
              <MenuItem
                component={BoxLink}
                href={item.location}
                key={item.id}
                tabIndex={0}
                onClick={() => {
                  setAnchorEl(null);
                  handleNavMenuHover?.(false); // Ensure sidebar is notified when an item is clicked
                }}
                onKeyDown={(event) => handleMenuKeyDown(event, index)}
              >
                <span>{item.name}</span>
              </MenuItem>
            ))
          )}
        </Paper>
      </Popper>
    </Stack>
  );
}
